import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import {
  BottomSheetView,
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetModalProps,
  BottomSheetBackdropProps,
  BottomSheetHandleProps,
} from '@gorhom/bottom-sheet';

import { Box, Lottie, useBreakpointProp } from '@bonanza-social/moonlight';

import BottomSheetBackground from './BottomSheetBackground';
import { createCustomBackdrop } from './BottomSheetBackdrop';
import BottomSheetHeader from './BottomSheetHeader';
import {
  BottomSheetBackgroundProps,
  BottomSheetHeaderProps,
  BottomSheetProps,
} from './BottomSheetModal.types';
import useAutoCloseOnNavigation from '../hooks/useAutoCloseOnNavigation/useAutoCloseOnNavigation';

function CustomBottomSheetModal(
  {
    light = false,
    borderStyle = 'top',
    borderGradient = 'accentBackground',
    headerImageName,
    showCloseIcon = true,
    title,
    titleKey,
    titleNS,
    scrollable,
    presentAnimation,
    closeOnBlur = true,
    children,
    close,
    detached = false,
    onInfo,
    autoCloseOnNavigation = true,
    contentPaddingHorizontal,
    testID,
    ...rest
  }: BottomSheetProps,
  ref: ForwardedRef<BottomSheetModal>,
) {
  const { width, height } = useWindowDimensions();
  const [isFullyOpened, setIsFullyOpened] = useState(false);
  const isLandscape = width > height;
  const presentAnimationRan = useRef(false);
  const ContentComponent = scrollable ? BottomSheetScrollView : BottomSheetView;
  const modalAdditionalProps = useBreakpointProp<
    Partial<BottomSheetModalProps>
  >({
    mobile: {
      detached,
    },
    tablet: { style: { marginHorizontal: (width - 500) / 2 } },
    desktop: {
      style: { maxWidth: 500, marginHorizontal: 'auto' },
      detached: true,
      bottomInset: 20,
    },
  });
  const handleComponent = useCallback(
    (props: BottomSheetHandleProps & BottomSheetHeaderProps) => (
      <BottomSheetHeader
        {...props}
        light={light}
        showCloseIcon={showCloseIcon}
        title={title}
        titleKey={titleKey}
        titleNS={titleNS}
        imageName={headerImageName}
        onInfo={onInfo}
      />
    ),
    [headerImageName, light, onInfo, showCloseIcon, title, titleKey, titleNS],
  );

  const backgroundComponent = useCallback(
    (props: BottomSheetBackgroundProps) => (
      <BottomSheetBackground
        {...props}
        light={light}
        borderStyle={borderStyle}
        borderGradient={borderGradient}
      />
    ),
    [borderGradient, borderStyle, light],
  );

  const backdropComponent = useCallback(
    (props: BottomSheetBackdropProps) => {
      const BackdropComponent = createCustomBackdrop(close);

      return (
        <BackdropComponent
          closeOnBlur={closeOnBlur}
          isFullyOpened={isFullyOpened}
          {...props}
        />
      );
    },
    [close, closeOnBlur, isFullyOpened],
  );

  useAutoCloseOnNavigation(autoCloseOnNavigation, close);

  const onChange = useCallback(
    (index: number) => {
      // When we have multiple indexes we want to present the animation only on the initial present
      if (!presentAnimationRan.current && index >= 0 && presentAnimation) {
        Lottie.present({ animation: presentAnimation });

        presentAnimationRan.current = true;
      }

      setIsFullyOpened(index >= 0);
    },
    [presentAnimation],
  );

  return (
    <BottomSheetModal
      ref={ref}
      {...modalAdditionalProps}
      handleComponent={handleComponent}
      backgroundComponent={backgroundComponent}
      backdropComponent={backdropComponent}
      onChange={onChange}
      {...rest}
    >
      <ContentComponent
        keyboardShouldPersistTaps="handled"
        testID={testID}
      >
        <Box
          marginHorizontal="space-600"
          marginBottom="space-600"
          paddingHorizontal={
            contentPaddingHorizontal ?? (isLandscape ? 'space-800' : undefined)
          }
          paddingBottom={Platform.select({
            android: 'safe-area-inset-bottom-pure',
            default: undefined,
          })}
        >
          {children}
        </Box>
      </ContentComponent>
    </BottomSheetModal>
  );
}

export default forwardRef(CustomBottomSheetModal);
